import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  Grid,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import apiClient from "../utils/apiClient";
import { useSelector } from "react-redux";
import { getImageFullUrl, pageRoute, siteData } from "../utils/common";
import FallbackImage from "../components/FallbackImage";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth()
  const navigate = useNavigate();
  const logo = useSelector((state) => state.logo);

  // ================= Password Validation =================
  const passwordValidation = (value) => {
    const regex =
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!%*?&#^]{8,}$/;
    return regex.test(value);
  };

  // ================= Form Submit =================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ email: "", password: "" });

    let newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (!passwordValidation(password)) {
      newErrors.password =
        "Min 8 chars, include uppercase, number & special char";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      const res = await apiClient.post("/api/vendor/login", { email, password });

      login(res?.data?.data?.token)

      // ✅ Show toast success message
      toast.success("Login Successful!");

    } catch (err) {
      if (err.response) {
        toast.error(err.response.data.message || "Login failed");
      } else {
        toast.error("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#ebebeb",
          px: 2,
          py: 2,
        }}
      >
        <Paper
          elevation={4}
          sx={{
            maxWidth: 900,
            width: "100%",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Grid container>
            {/* Left Side - Logo & Branding */}
            <Grid
              item
              size={{ xs: 12, md: 5 }}

              sx={{
                bgcolor: "primary.main",
                color: "white",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                px: 3,
                textAlign: "center",
              }}
            >
              <FallbackImage
                src={'/logo.png'}
                style={{ maxHeight: "80px", marginBottom: "20px" }}
              />
              <Typography variant="h4" fontWeight="bold">
                {siteData.name}
              </Typography>
              <Typography variant="body1" mt={2} sx={{ opacity: 0.85 }}>
                Welcome to the Vendor Panel.
                Sign in to manage your dashboard and access advanced tools.
              </Typography>
            </Grid>

            {/* Right Side - Signup Form */}
            <Grid item size={{ xs: 12, md: 7 }}
              sx={{ p: 4 }}>
              <h1 style={{ textAlign: 'center' }}>Login Account</h1>
              <form onSubmit={handleSubmit}>
                {/* Email */}
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                />

                {/* Password */}
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Link to="/forgot-password" style={{ textDecoration: "none", color: "#1976d2" }} >Forgot Password?</Link>
                {/* Submit Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 1, mb: 1, py: 1.2, fontWeight: "bold" }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Login"}
                </Button>
              </form>
              <Typography textAlign="center" sx={{ mt: 2 }}>
                Don’t have an account?{" "}
                <Link to={pageRoute.signUp} style={{ textDecoration: "none", color: "#1976d2" }}>
                  Sign Up
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>

    </>
  );
};

export default LoginPage;
